import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './i18n/config'; // Initialize i18n
import App from './App';
import reportWebVitals from './reportWebVitals';
import { preloadIconsInBackground } from './utils/iconPreloader';
import { isExtensionInterceptedFetchError, isBrowserExtensionNoise } from './utils/runtimeNoise';

// Preload icons in the background for instant loading
preloadIconsInBackground();

// Handle chunk loading errors globally - set up BEFORE React renders
// This catches errors that happen during initial load
const handleChunkError = (event) => {
  const errorMessage = event.message || event.error?.message || '';
  const errorName = event.error?.name || '';
  const targetSrc = event.target?.src || event.target?.href || '';
  
  const isChunkError = 
    errorName === 'ChunkLoadError' ||
    errorMessage.includes('Loading chunk') ||
    errorMessage.includes('ChunkLoadError') ||
    errorMessage.includes('Failed to fetch dynamically imported module') ||
    errorMessage.includes('Loading CSS chunk') ||
    errorMessage.includes('Loading JS chunk') ||
    (errorMessage.includes('chunk') && errorMessage.includes('failed')) ||
    (targetSrc && (
      targetSrc.includes('/static/css/') || 
      targetSrc.includes('/static/js/') ||
      targetSrc.includes('.chunk.')
    ));

  if (isChunkError) {
    console.warn('Chunk loading error detected, reloading page...', event);
    // Prevent infinite reload loop by checking if we've already tried to reload
    if (!sessionStorage.getItem('chunk-reload-attempted')) {
      sessionStorage.setItem('chunk-reload-attempted', 'true');
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } else {
      // If reload already attempted, clear the flag after a delay
      setTimeout(() => {
        sessionStorage.removeItem('chunk-reload-attempted');
      }, 5000);
      console.error('Chunk loading failed after reload attempt');
    }
  }
};

// Handle OAuth hash redirects IMMEDIATELY before React renders
// This catches hash-based OAuth redirects from Supabase
(function handleOAuthHashImmediate() {
  const hash = window.location.hash;
  if (hash && hash.includes('access_token=')) {
    // Prevent React from rendering until we process the hash
    const hashParams = new URLSearchParams(hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    const expiresAt = hashParams.get('expires_at');
    const expiresIn = hashParams.get('expires_in');
    
    if (accessToken) {
      // Store session immediately
      let expiresAtTimestamp = null;
      if (expiresAt) {
        expiresAtTimestamp = parseInt(expiresAt);
      } else if (expiresIn) {
        expiresAtTimestamp = Math.floor(Date.now() / 1000) + parseInt(expiresIn);
      } else {
        expiresAtTimestamp = Math.floor(Date.now() / 1000) + 3600;
      }
      
      localStorage.setItem('auth_token', accessToken);
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }
      localStorage.setItem('expires_at', expiresAtTimestamp.toString());
      
      // Extract user ID from JWT token immediately for instant UI update
      try {
        const tokenParts = accessToken.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          if (payload.sub) {
            localStorage.setItem('user_id', payload.sub);
            if (payload.email) {
              localStorage.setItem('user_email', payload.email);
            }
            // Extract username from metadata if available
            if (payload.user_metadata?.name) {
              localStorage.setItem('user_username', payload.user_metadata.name);
            } else if (payload.user_metadata?.full_name) {
              localStorage.setItem('user_username', payload.user_metadata.full_name.split(' ')[0]);
            }
            if (payload.user_metadata?.avatar_url || payload.user_metadata?.picture) {
              localStorage.setItem('user_avatar_url', payload.user_metadata.avatar_url || payload.user_metadata.picture);
            }
          }
        }
      } catch (e) {
        console.warn('Could not decode JWT token:', e);
      }
      
      // Dispatch login event IMMEDIATELY so Header updates before page renders
      window.dispatchEvent(new CustomEvent('user-login'));
      
      // Clear hash immediately to prevent React Router from seeing it
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      
      // Set flag so OAuthHashHandler knows to fetch full user data
      sessionStorage.setItem('oauth_pending', 'true');
      sessionStorage.setItem('oauth_access_token', accessToken);
    }
  }
})();

// Set up error handlers immediately
window.addEventListener(
  'error',
  (event) => {
    if (!isBrowserExtensionNoise(event.error || event, event)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  },
  true
);

window.addEventListener('error', handleChunkError, true); // Use capture phase

window.addEventListener(
  'unhandledrejection',
  (event) => {
    if (!isExtensionInterceptedFetchError(event.reason, event)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  },
  true
);

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const errorMessage = reason?.message || '';
  const errorName = reason?.name || '';
  
  const isChunkError = 
    errorName === 'ChunkLoadError' ||
    (errorMessage && (
      errorMessage.includes('Loading chunk') ||
      errorMessage.includes('ChunkLoadError') ||
      errorMessage.includes('Failed to fetch dynamically imported module') ||
      errorMessage.includes('Loading CSS chunk') ||
      errorMessage.includes('Loading JS chunk') ||
      (errorMessage.includes('chunk') && errorMessage.includes('failed'))
    ));

  if (isChunkError) {
    console.warn('Chunk loading error detected in promise rejection, reloading page...', reason);
    event.preventDefault();
    if (!sessionStorage.getItem('chunk-reload-attempted')) {
      sessionStorage.setItem('chunk-reload-attempted', 'true');
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } else {
      setTimeout(() => {
        sessionStorage.removeItem('chunk-reload-attempted');
      }, 5000);
      console.error('Chunk loading failed after reload attempt');
    }
  }
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
